import { createTheme, responsiveFontSizes } from '@material-ui/core/styles';

// Color manipulation utilities
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const lightenColor = (color, amount) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const newR = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount));
  const newG = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount));
  const newB = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount));
  
  return rgbToHex(newR, newG, newB);
};

const darkenColor = (color, amount) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const newR = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const newG = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const newB = Math.max(0, Math.round(rgb.b * (1 - amount)));
  
  return rgbToHex(newR, newG, newB);
};

// Main color definitions - change these to update the entire theme
// These colors will automatically generate light and dark variants

// Generate dynamic color variants
const generateColorPalette = (mainColor) => ({
  light: lightenColor(mainColor, 0.3),
  main: mainColor,
  dark: darkenColor(mainColor, 0.2),
  contrastText: getContrastText(mainColor)
});

const getContrastText = (color) => {
  const rgb = hexToRgb(color);
  if (!rgb) return '#000000';
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};


// Determine theme type based on background color
const getThemeType = (backgroundColor) => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return 'dark';
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? 'light' : 'dark';
};

const theme = (THEME_COLORS) => {
  const themeType = getThemeType(THEME_COLORS.BACKGROUND_MAIN);
  
  return createTheme({
    typography: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: 12,
      letterSpacing: '0.01rem'
    },
    palette: {
      type: themeType,
    common: {
      black: 'rgba(0, 0, 0, 1)',
      white: 'rgba(255, 255, 255, 1)',
      green: '#03AF4B',
      yellow: '#FEC92F',
      red: '#FF0000',
      orange: '#FF6E00',
      blue: '#1188C9',
    },
    background: { 
      paper: THEME_COLORS.BACKGROUND_SECONDARY, 
      default: THEME_COLORS.BACKGROUND_MAIN 
    },
    primary: generateColorPalette(THEME_COLORS.PRIMARY_MAIN),
    secondary: generateColorPalette(THEME_COLORS.SECONDARY_MAIN),
    error: {
      light: themeType === 'light' ? '#e57373' : '#d32f2f',
      main: '#f44336',
      dark: themeType === 'light' ? '#d32f2f' : '#e57373',
      contrastText: themeType === 'light' ? '#fff' : '#000'
    },
    text: {
      primary: THEME_COLORS.PRIMARY_MAIN,
      secondary: THEME_COLORS.SECONDARY_MAIN,
      disabled: lightenColor(THEME_COLORS.SECONDARY_MAIN, 0.4),
      hint: darkenColor(THEME_COLORS.SECONDARY_MAIN, 0.3)
    }
  },
  overrides: {
    MuiFormLabel: {
      root: {
        color: THEME_COLORS.PRIMARY_MAIN
      }
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: THEME_COLORS.BACKGROUND_MAIN,
        color: THEME_COLORS.PRIMARY_MAIN
      }
    },
    MuiDrawer: {
      paperAnchorDockedLeft: {
        borderRight: 0
      }
    },
    MuiButton: {
      root: {
        textTransform: 'capitalize',
        borderRadius: 0
      }
    },
    MuiTableHead: {
      root: {
        background: THEME_COLORS.BACKGROUND_MAIN,
        padding: '1rem'
      }
    },
    MuiTableBody: {
      root: {
        marginTop: '1rem'
      }
    },
    MuiTableCell: {
      root: {
        borderBottom: 'none'
      }
    }
  }
  });
};

// Create the theme function that accepts THEME_COLORS
const createDynamicTheme = (THEME_COLORS) => responsiveFontSizes(theme(THEME_COLORS));

// Export the theme function
export default createDynamicTheme;
